import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { formatNumber } from "@/lib/mrp";
import { formatQuantityWithUnit } from "@/lib/mrpSkuUnitDisplay";
import { fetchInboundReceiptReview } from "./inboundApi";

function formatWhen(raw: string | null): string {
  if (raw == null || raw.trim() === "") return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString();
}

function formatQuantity(raw: string): string {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return raw;
  return formatNumber(parsed);
}

function containerLabel(unitKind: string, containerNo: number): string {
  const kind = unitKind.trim() === "" ? "Container" : unitKind.charAt(0).toUpperCase() + unitKind.slice(1);
  return `${kind} ${String(containerNo)}`;
}

export function InboundReceiptReviewPage() {
  const { receiptId = "" } = useParams<{ receiptId: string }>();
  const receiptQuery = useQuery({
    queryKey: ["shipping", "inbound-receipt-review", receiptId],
    queryFn: () => fetchInboundReceiptReview(receiptId),
    enabled: receiptId.trim() !== "",
  });
  const receipt = receiptQuery.data;
  const unitBySku = new Map((receipt?.lines ?? []).map((line) => [line.sku, line.unit_of_measure]));
  const title = receiptId.trim() !== "" ? `Receipt ${receiptId}` : "Receipt";
  const sourceLabel = receipt?.source_number?.trim() ?? "";

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Quantities and containers recorded when this receipt was received. This page does not start receiving labor.">
        {title}
      </PageTitleWithHelp>
      {receiptQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading receipt…
        </p>
      ) : null}
      {receiptQuery.isError ? (
        <p role="alert" className="text-sm text-rose-700">
          {receiptQuery.error instanceof Error ? receiptQuery.error.message : "Could not load this receipt."}
        </p>
      ) : null}
      {receipt != null ? (
        <>
          <p className="text-sm text-slate-600">
            {sourceLabel !== "" ? sourceLabel : `Source ${receipt.source_id}`}
            {receipt.status.trim() !== "" ? ` · ${receipt.status.replaceAll("_", " ")}` : ""}
            {receipt.received_at != null ? ` · Received ${formatWhen(receipt.received_at)}` : ""}
          </p>
          <Card>
            <CardHeader>
              <CardTitle>Received</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              {receipt.lines.length === 0 ? (
                <p className="px-4 py-3 text-sm text-slate-600">No received quantities were recorded on this receipt.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-2">SKU</th>
                      <th className="px-4 py-2">Description</th>
                      <th className="px-4 py-2 text-right">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipt.lines.map((line, index) => (
                      <tr key={`${line.sku}-${String(index)}`} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-4 py-2 font-medium text-slate-900">{line.sku}</td>
                        <td className="px-4 py-2 text-slate-700">{line.description?.trim() || "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatQuantityWithUnit(formatQuantity(line.received_quantity), line.unit_of_measure)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
          {receipt.subcontainers.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Labeled containers</CardTitle>
              </CardHeader>
              <CardBody className="space-y-3">
                {receipt.subcontainers.map((container) => (
                  <div key={container.id}>
                    <p className="text-sm font-medium text-slate-900">
                      {containerLabel(container.unit_kind, container.subcontainer_no)}
                    </p>
                    {container.items.length === 0 ? (
                      <p className="mt-1 text-sm text-slate-600">No lines in this container.</p>
                    ) : (
                      <ul className="mt-1 space-y-1 text-sm text-slate-700">
                        {container.items.map((item) => (
                          <li key={`${container.id}-${item.source_item_id}-${item.sku}`}>
                            {item.sku} · {formatQuantityWithUnit(formatQuantity(item.quantity), unitBySku.get(item.sku))}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}
          {receipt.notes != null && receipt.notes.trim() !== "" ? (
            <p className="whitespace-pre-wrap text-sm text-slate-700">{receipt.notes}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
